import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight client-side error logger.
 *
 * Design rules:
 * - Never throws and never blocks rendering. If logging fails, the app behaves
 *   exactly as before.
 * - Throttled per session (max MAX_PER_SESSION) and de-duplicated within a
 *   one-minute window, so an error loop can never flood the database.
 * - Filters out cross-origin / browser-extension noise ("Script error." with
 *   no stack), which otherwise drowns out real application errors.
 */

const MAX_PER_SESSION = 8;
const DEDUPE_WINDOW_MS = 60_000;
const MESSAGE_MAX = 500;
const STACK_MAX = 5000;

let sentCount = 0;
const recent = new Map<string, number>();

function isNoise(message: string, stack?: string | null): boolean {
  const m = (message || "").trim();
  if (!m) return true;
  // Classic cross-origin / extension signal
  if (/^script error\.?$/i.test(m) && !stack) return true;
  if (/^ResizeObserver loop/i.test(m)) return true;
  if (stack && /(chrome-extension|moz-extension|safari-extension):\/\//i.test(stack)) return true;
  return false;
}

/** Maakt wachtwoorden en tokens onleesbaar voordat iets wordt opgeslagen. */
export function redact(input: string): string {
  return input
    .replace(/(["']?(?:password|passwd|pwd|access_token|refresh_token|token|secret)["']?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,&}]+)/gi, "$1[redacted]")
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
    .replace(/eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, "[redacted-jwt]");
}

export async function logClientError(
  error: unknown,
  source: string,
  extra?: { componentStack?: string | null }
): Promise<void> {
  try {
    const err = error as { message?: string; stack?: string } | undefined;
    const rawMessage =
      typeof error === "string" ? error : err?.message || String(error ?? "Unknown error");
    const rawStack = [err?.stack, extra?.componentStack].filter(Boolean).join("\n\n") || null;

    if (isNoise(rawMessage, rawStack)) return;
    if (sentCount >= MAX_PER_SESSION) return;

    const key = `${source}|${rawMessage}`;
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    recent.set(key, now);
    sentCount += 1;

    let userId: string | null = null;
    try {
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user?.id ?? null;
    } catch {
      // ignore — logging works for anonymous visitors too
    }

    await supabase.from("client_error_log").insert({
      message: redact(rawMessage).slice(0, MESSAGE_MAX),
      stack: rawStack ? redact(rawStack).slice(0, STACK_MAX) : null,
      route: typeof window !== "undefined" ? window.location.pathname.slice(0, 300) : null,
      user_agent:
        typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 400) : null,
      source: source.slice(0, 60),
      user_id: userId,
    });
  } catch {
    // Swallow everything: logging must never affect the app.
  }
}

let initialized = false;

export function initGlobalErrorLogging(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (event) => {
    const err = event.error as Error | undefined;
    const message = err?.message || event.message || "";
    const stack = err?.stack || null;
    if (isNoise(message, stack)) return;
    void logClientError(err ?? message, "window.onerror");
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    void logClientError(reason, "unhandledrejection");
  });
}
